import React, { useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useSignIn, useSignUp } from '@clerk/clerk-react';
import { useUIStore } from '@/stores/uiStore';

// ─── Field ────────────────────────────────────────────────────────────────────

const Field: React.FC<{
  label: string;
  name: string;
  type?: string;
  placeholder?: string;
  autoComplete?: string;
  value: string;
  error?: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}> = ({ label, name, type = 'text', placeholder, autoComplete, value, error, onChange }) => (
  <div className="flex flex-col gap-1.5">
    <label
      className="text-xs font-bold uppercase tracking-widest"
      style={{ color: 'var(--text-muted)', fontFamily: 'var(--font-display)', letterSpacing: '0.16em' }}
    >
      {label}
    </label>
    <input
      name={name}
      type={type}
      autoComplete={autoComplete}
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      className="w-full rounded px-4 py-3 text-base outline-none transition-all"
      style={{
        background: 'var(--bg-deep)',
        border: error ? '1px solid #ef4444' : '1px solid var(--border-mid)',
        color: 'var(--text)',
        fontFamily: 'var(--font-body)',
      }}
      onFocus={e => { e.currentTarget.style.borderColor = 'var(--crimson)'; e.currentTarget.style.boxShadow = '0 0 0 3px var(--crimson-dim)'; }}
      onBlur={e => { e.currentTarget.style.borderColor = error ? '#ef4444' : 'var(--border-mid)'; e.currentTarget.style.boxShadow = 'none'; }}
    />
    {error && <p className="text-xs text-red-400">{error}</p>}
  </div>
);

// ─── LoginPage ────────────────────────────────────────────────────────────────

type Mode = 'login' | 'signup' | 'verify' | 'mfa' | 'forgot' | 'reset';

const LoginPage: React.FC = () => {
  const history = useHistory();
  const addToast = useUIStore((s) => s.addToast);

  const { signIn, setActive: setSignInActive, isLoaded: signInLoaded } = useSignIn();
  const { signUp, setActive: setSignUpActive, isLoaded: signUpLoaded } = useSignUp();

  const [mode, setMode] = useState<Mode>('login');
  const [formData, setFormData] = useState({ name: '', email: '', password: '', confirmPassword: '' });
  const [code, setCode] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [mfaStrategy, setMfaStrategy] = useState<'totp' | 'backup_code' | 'phone_code'>('totp');
  const [phonePrepared, setPhonePrepared] = useState(false);
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const codeRef = useRef<HTMLInputElement>(null);
  const resetCodeRef = useRef<HTMLInputElement>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  // ─── Sign in ──────────────────────────────────────────────────────────────

  const handleSignIn = async () => {
    if (!signInLoaded || !signIn) return;
    const result = await signIn.create({ identifier: formData.email, password: formData.password });
    if (result.status === 'needs_second_factor') { setMode('mfa'); return; }
    if (result.status === 'complete') {
      await setSignInActive({ session: result.createdSessionId });
      history.replace('/dashboard');
    } else {
      addToast({ title: 'Sign in failed', description: `Status: ${result.status}`, variant: 'error' });
    }
  };

  // ─── MFA ─────────────────────────────────────────────────────────────────

  const handleSelectMfaStrategy = async (strategy: 'totp' | 'backup_code' | 'phone_code') => {
    setMfaStrategy(strategy);
    setMfaCode('');
    setErrors({});
    setPhonePrepared(false);
    if (strategy === 'phone_code' && signIn) {
      try {
        const phoneFactors = signIn.supportedSecondFactors?.filter((f) => f.strategy === 'phone_code');
        const phoneNumberId = (phoneFactors?.[0] as { phoneNumberId?: string } | undefined)?.phoneNumberId;
        await signIn.prepareSecondFactor({ strategy: 'phone_code', phoneNumberId });
        setPhonePrepared(true);
        addToast({ title: 'Code sent', description: 'Check your phone.', variant: 'info' });
      } catch (err) {
        const clerkErr = err as { errors?: Array<{ message: string }> };
        addToast({ title: 'Could not send code', description: clerkErr?.errors?.[0]?.message ?? 'Failed to send SMS code.', variant: 'error' });
      }
    }
  };

  const handleMfaSubmit = async () => {
    if (!signIn) return;
    const result = await signIn.attemptSecondFactor(
      mfaStrategy === 'backup_code' ? { strategy: 'backup_code', code: mfaCode }
      : mfaStrategy === 'phone_code' ? { strategy: 'phone_code', code: mfaCode }
      : { strategy: 'totp', code: mfaCode }
    );
    if (result.status === 'complete') {
      await setSignInActive({ session: result.createdSessionId });
      history.replace('/dashboard');
    } else {
      addToast({ title: 'Verification failed', description: `Status: ${result.status}`, variant: 'error' });
    }
  };

  // ─── Sign up ──────────────────────────────────────────────────────────────

  const handleSignUp = async () => {
    if (!signUpLoaded || !signUp) return;
    if (formData.password !== formData.confirmPassword) { setErrors({ confirmPassword: 'Passwords do not match' }); return; }
    const result = await signUp.create({
      emailAddress: formData.email,
      password: formData.password,
      firstName: formData.name.split(' ')[0] || undefined,
      lastName: formData.name.split(' ').slice(1).join(' ') || undefined,
    });
    if (result.status === 'complete') {
      await setSignUpActive({ session: result.createdSessionId });
      history.replace('/dashboard');
    } else if (result.status === 'missing_requirements') {
      await signUp.prepareEmailAddressVerification({ strategy: 'email_code' });
      setMode('verify');
      setTimeout(() => codeRef.current?.focus(), 100);
    }
  };

  // ─── Verify ───────────────────────────────────────────────────────────────

  const handleVerify = async () => {
    if (!signUpLoaded || !signUp) return;
    const result = await signUp.attemptEmailAddressVerification({ code });
    if (result.status === 'complete') {
      await setSignUpActive({ session: result.createdSessionId });
      addToast({ title: 'Account created', variant: 'success' });
      history.replace('/dashboard');
    } else {
      addToast({ title: 'Verification incomplete', description: `Status: ${result.status}`, variant: 'warning' });
    }
  };

  const handleResend = async () => {
    if (!signUpLoaded || !signUp) return;
    try {
      await signUp.prepareEmailAddressVerification({ strategy: 'email_code' });
      addToast({ title: 'Code resent', description: 'Check your inbox.', variant: 'info' });
    } catch { addToast({ title: 'Could not resend', variant: 'error' }); }
  };

  // ─── Forgot password ──────────────────────────────────────────────────────

  const handleForgotPassword = async () => {
    if (!signInLoaded || !signIn) return;
    if (!formData.email) { setErrors({ email: 'Enter your email address' }); return; }
    const result = await signIn.create({
      strategy: 'reset_password_email_code',
      identifier: formData.email,
    });
    if (result.status === 'needs_first_factor') {
      setMode('reset');
      setResetCode('');
      setNewPassword('');
      setConfirmNewPassword('');
      setTimeout(() => resetCodeRef.current?.focus(), 100);
    } else {
      addToast({ title: 'Unexpected response', description: `Status: ${result.status}`, variant: 'warning' });
    }
  };

  // ─── Reset password ───────────────────────────────────────────────────────

  const handleResetPassword = async () => {
    if (!signInLoaded || !signIn) return;
    if (newPassword !== confirmNewPassword) { setErrors({ confirmNewPassword: 'Passwords do not match' }); return; }
    const attempt = await signIn.attemptFirstFactor({
      strategy: 'reset_password_email_code',
      code: resetCode,
    });
    if (attempt.status === 'needs_new_password') {
      const result = await (signIn as any).resetPassword({ password: newPassword });
      if (result.status === 'complete') {
        await setSignInActive({ session: result.createdSessionId });
        addToast({ title: 'Password updated', variant: 'success' });
        history.replace('/dashboard');
      } else {
        addToast({ title: 'Reset incomplete', description: `Status: ${result.status}`, variant: 'warning' });
      }
    } else if (attempt.status === 'complete') {
      await setSignInActive({ session: attempt.createdSessionId });
      history.replace('/dashboard');
    } else {
      addToast({ title: 'Invalid code', description: 'Check the code and try again.', variant: 'error' });
    }
  };

  // ─── Submit dispatcher ────────────────────────────────────────────────────

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    if (mode === 'verify') {
      if (!code.trim()) { setErrors({ code: 'Enter the 6-digit code' }); return; }
    } else if (mode === 'mfa') {
      if (!mfaCode.trim()) { setErrors({ mfaCode: 'Enter your code' }); return; }
      if (mfaStrategy !== 'backup_code' && mfaCode.length < 6) { setErrors({ mfaCode: 'Enter the 6-digit code' }); return; }
    } else if (mode === 'forgot') {
      if (!formData.email) { setErrors({ email: 'Enter your email address' }); return; }
    } else if (mode === 'reset') {
      if (resetCode.length < 6) { setErrors({ resetCode: 'Enter the 6-digit code' }); return; }
      if (!newPassword) { setErrors({ newPassword: 'Enter a new password' }); return; }
      if (newPassword !== confirmNewPassword) { setErrors({ confirmNewPassword: 'Passwords do not match' }); return; }
    } else {
      if (!formData.email) { setErrors((p) => ({ ...p, email: 'Required' })); return; }
      if (!formData.password) { setErrors((p) => ({ ...p, password: 'Required' })); return; }
    }

    setIsLoading(true);
    try {
      if (mode === 'login') await handleSignIn();
      else if (mode === 'signup') await handleSignUp();
      else if (mode === 'mfa') await handleMfaSubmit();
      else if (mode === 'forgot') await handleForgotPassword();
      else if (mode === 'reset') await handleResetPassword();
      else await handleVerify();
    } catch (err: unknown) {
      const clerkErr = err as { errors?: Array<{ code?: string; message: string; longMessage?: string }>; status?: string };
      const needsMfa = signIn?.status === 'needs_second_factor' || clerkErr?.status === 'needs_second_factor' || clerkErr?.errors?.some((e) => e.code?.includes('second_factor'));
      if (needsMfa && mode === 'login') { setMode('mfa'); return; }
      const message = clerkErr?.errors?.[0]?.longMessage ?? clerkErr?.errors?.[0]?.message ?? (err instanceof Error ? err.message : 'Something went wrong');
      addToast({
        title: mode === 'login' ? 'Sign in failed' : mode === 'signup' ? 'Sign up failed' : mode === 'forgot' ? 'Could not send reset email' : mode === 'reset' ? 'Reset failed' : 'Verification failed',
        description: message,
        variant: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const switchMode = (next: 'login' | 'signup') => {
    setMode(next); setErrors({}); setCode(''); setMfaCode(''); setMfaStrategy('totp'); setPhonePrepared(false);
    setResetCode(''); setNewPassword(''); setConfirmNewPassword('');
  };

  // ─── Spinner ──────────────────────────────────────────────────────────────

  const Spinner = () => (
    <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
    </svg>
  );

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden"
      style={{ background: 'var(--bg-black)' }}
    >
          {/* Background radial glow */}
          <div className="absolute inset-0 pointer-events-none" style={{
            background: 'radial-gradient(ellipse at 65% 40%, rgba(192,57,43,0.10) 0%, transparent 55%), radial-gradient(ellipse at 20% 70%, rgba(192,57,43,0.05) 0%, transparent 45%)',
          }} />
          {/* Dot grid */}
          <div className="absolute inset-0 opacity-[0.04] pointer-events-none" style={{
            backgroundImage: 'radial-gradient(circle at 1px 1px, #f0f0f5 1px, transparent 0)',
            backgroundSize: '36px 36px',
          }} />

          <div className="relative w-full max-w-sm z-10">

            {/* Logo mark */}
            <div className="text-center mb-10">
              <div className="inline-flex items-center justify-center w-14 h-14 mb-5" style={{
                background: 'var(--crimson)',
                borderRadius: '10px',
                boxShadow: '0 0 28px rgba(192,57,43,0.5)',
              }}>
                <svg className="w-8 h-8 text-white" viewBox="0 0 32 32" fill="none" aria-hidden="true">
                  <circle cx="16" cy="16" r="3" fill="currentColor" />
                  <circle cx="6" cy="10" r="2" fill="currentColor" opacity="0.85" />
                  <circle cx="26" cy="10" r="2" fill="currentColor" opacity="0.85" />
                  <circle cx="6" cy="22" r="2" fill="currentColor" opacity="0.6" />
                  <circle cx="26" cy="22" r="2" fill="currentColor" opacity="0.6" />
                  <line x1="16" y1="16" x2="6" y2="10" stroke="currentColor" strokeWidth="1.5" opacity="0.65" />
                  <line x1="16" y1="16" x2="26" y2="10" stroke="currentColor" strokeWidth="1.5" opacity="0.65" />
                  <line x1="16" y1="16" x2="6" y2="22" stroke="currentColor" strokeWidth="1.5" opacity="0.45" />
                  <line x1="16" y1="16" x2="26" y2="22" stroke="currentColor" strokeWidth="1.5" opacity="0.45" />
                </svg>
              </div>
              <h1
                className="text-4xl font-black uppercase tracking-widest"
                style={{ fontFamily: 'var(--font-display)', color: 'var(--text)', letterSpacing: '0.18em' }}
              >
                CapOS
              </h1>
              <p className="mt-1 text-xs uppercase tracking-widest" style={{ color: 'var(--text-muted)', fontFamily: 'var(--font-display)', fontWeight: 600, letterSpacing: '0.22em' }}>
                Capability Operating System
              </p>
            </div>

            {/* Card */}
            <div style={{
              background: 'var(--bg-base)',
              border: '1px solid var(--border-mid)',
              borderRadius: '8px',
              boxShadow: '0 8px 40px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.05)',
              padding: '2rem',
            }}>

              {mode === 'forgot' ? (
                /* ── Forgot password ── */
                <div className="flex flex-col gap-5">
                  <div>
                    <p className="eyebrow mb-3">Reset password</p>
                    <p className="text-sm" style={{ color: 'var(--text-dim)' }}>
                      Enter your email and we'll send a reset code.
                    </p>
                  </div>
                  <form onSubmit={handleSubmit} className="flex flex-col gap-4" noValidate>
                    <Field
                      label="Email"
                      name="email"
                      type="email"
                      autoComplete="email"
                      placeholder="you@example.com"
                      value={formData.email}
                      error={errors['email']}
                      onChange={handleChange}
                    />
                    <button type="submit" disabled={isLoading || !signInLoaded} className="btn-primary w-full justify-center">
                      {isLoading ? <><Spinner /> Sending…</> : 'Send reset code'}
                    </button>
                  </form>
                  <div className="text-center text-xs" style={{ color: 'var(--text-muted)' }}>
                    <button type="button" onClick={() => switchMode('login')} style={{ color: 'var(--crimson-light)' }} className="hover:underline">
                      Back to sign in
                    </button>
                  </div>
                </div>

              ) : mode === 'reset' ? (
                /* ── Reset password ── */
                <div className="flex flex-col gap-5">
                  <div>
                    <p className="eyebrow mb-3">Set new password</p>
                    <p className="text-sm" style={{ color: 'var(--text-dim)' }}>
                      Enter the code sent to <span style={{ color: 'var(--text)' }}>{formData.email}</span> and choose a new password.
                    </p>
                  </div>
                  <form onSubmit={handleSubmit} className="flex flex-col gap-4" noValidate>
                    <div className="flex flex-col gap-1.5">
                      <label className="text-xs font-bold uppercase" style={{ color: 'var(--text-muted)', fontFamily: 'var(--font-display)', letterSpacing: '0.16em' }}>
                        Reset code
                      </label>
                      <input
                        ref={resetCodeRef}
                        type="text"
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        maxLength={6}
                        placeholder="000000"
                        value={resetCode}
                        onChange={(e) => { setResetCode(e.target.value.replace(/\D/g, '')); setErrors({}); }}
                        className="w-full rounded px-4 py-3 text-center text-xl font-mono tracking-widest outline-none transition-all"
                        style={{ background: 'var(--bg-deep)', border: errors['resetCode'] ? '1px solid #ef4444' : '1px solid var(--border-mid)', color: 'var(--text)' }}
                      />
                      {errors['resetCode'] && <p className="text-xs text-red-400">{errors['resetCode']}</p>}
                    </div>
                    <Field
                      label="New password"
                      name="newPassword"
                      type="password"
                      autoComplete="new-password"
                      placeholder="••••••••"
                      value={newPassword}
                      error={errors['newPassword']}
                      onChange={(e) => { setNewPassword(e.target.value); setErrors((p) => ({ ...p, newPassword: '' })); }}
                    />
                    <Field
                      label="Confirm new password"
                      name="confirmNewPassword"
                      type="password"
                      autoComplete="new-password"
                      placeholder="••••••••"
                      value={confirmNewPassword}
                      error={errors['confirmNewPassword']}
                      onChange={(e) => { setConfirmNewPassword(e.target.value); setErrors((p) => ({ ...p, confirmNewPassword: '' })); }}
                    />
                    <button
                      type="submit"
                      disabled={isLoading || resetCode.length < 6 || !newPassword || !confirmNewPassword}
                      className="btn-primary w-full justify-center"
                    >
                      {isLoading ? <><Spinner /> Resetting…</> : 'Set new password'}
                    </button>
                  </form>
                  <div className="flex justify-between text-xs" style={{ color: 'var(--text-muted)' }}>
                    <button
                      type="button"
                      onClick={() => setMode('forgot')}
                      style={{ color: 'var(--crimson-light)' }}
                      className="hover:underline"
                    >
                      Resend code
                    </button>
                    <button type="button" onClick={() => switchMode('login')} className="hover:underline">
                      Back
                    </button>
                  </div>
                </div>

              ) : mode === 'verify' ? (
                /* ── Email verification ── */
                <div className="flex flex-col gap-5">
                  <div>
                    <p className="eyebrow mb-3">Verify email</p>
                    <p className="text-sm" style={{ color: 'var(--text-dim)' }}>
                      We sent a 6-digit code to <span style={{ color: 'var(--text)' }}>{formData.email}</span>.
                    </p>
                  </div>
                  <form onSubmit={handleSubmit} className="flex flex-col gap-4" noValidate>
                    <div className="flex flex-col gap-1.5">
                      <label className="text-xs font-bold uppercase" style={{ color: 'var(--text-muted)', fontFamily: 'var(--font-display)', letterSpacing: '0.16em' }}>
                        Verification code
                      </label>
                      <input
                        ref={codeRef}
                        type="text"
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        maxLength={6}
                        placeholder="000000"
                        value={code}
                        onChange={(e) => { setCode(e.target.value.replace(/\D/g, '')); setErrors({}); }}
                        className="w-full rounded px-4 py-3 text-center text-xl font-mono tracking-widest outline-none transition-all"
                        style={{ background: 'var(--bg-deep)', border: '1px solid var(--border-mid)', color: 'var(--text)' }}
                      />
                      {errors['code'] && <p className="text-xs text-red-400">{errors['code']}</p>}
                    </div>
                    <button type="submit" disabled={isLoading || code.length < 6} className="btn-primary w-full justify-center">
                      {isLoading ? <><Spinner /> Verifying…</> : 'Verify email'}
                    </button>
                  </form>
                  <div className="flex justify-between text-xs" style={{ color: 'var(--text-muted)' }}>
                    <button type="button" onClick={handleResend} style={{ color: 'var(--crimson-light)' }} className="hover:underline">Resend code</button>
                    <button type="button" onClick={() => switchMode('signup')} className="hover:underline">Back</button>
                  </div>
                </div>

              ) : mode === 'mfa' ? (
                /* ── MFA ── */
                <div className="flex flex-col gap-5">
                  <div>
                    <p className="eyebrow mb-3">Two-factor auth</p>
                    <p className="text-sm" style={{ color: 'var(--text-dim)' }}>
                      {mfaStrategy === 'totp' && 'Enter the code from your authenticator app.'}
                      {mfaStrategy === 'phone_code' && (phonePrepared ? 'Enter the code sent to your phone.' : 'Sending a code to your phone…')}
                      {mfaStrategy === 'backup_code' && 'Enter one of your backup codes.'}
                    </p>
                  </div>

                  {/* Strategy selector */}
                  {(() => {
                    const supported = signIn?.supportedSecondFactors ?? [];
                    const opts = [
                      supported.some(f => f.strategy === 'totp')        && { value: 'totp'        as const, label: 'Authenticator' },
                      supported.some(f => f.strategy === 'phone_code')  && { value: 'phone_code'  as const, label: 'SMS code'      },
                      supported.some(f => f.strategy === 'backup_code') && { value: 'backup_code' as const, label: 'Backup code'   },
                    ].filter(Boolean) as Array<{ value: 'totp' | 'backup_code' | 'phone_code'; label: string }>;
                    if (opts.length <= 1) return null;
                    return (
                      <div className="flex gap-2 flex-wrap">
                        {opts.map(opt => (
                          <button
                            key={opt.value}
                            type="button"
                            onClick={() => handleSelectMfaStrategy(opt.value)}
                            className="px-3 py-1.5 text-xs font-bold uppercase rounded transition-all"
                            style={{
                              fontFamily: 'var(--font-display)',
                              letterSpacing: '0.1em',
                              background: mfaStrategy === opt.value ? 'var(--crimson-dim)' : 'var(--bg-deep)',
                              border: mfaStrategy === opt.value ? '1px solid var(--crimson-border)' : '1px solid var(--border-mid)',
                              color: mfaStrategy === opt.value ? 'var(--crimson-light)' : 'var(--text-dim)',
                            }}
                          >
                            {opt.label}
                          </button>
                        ))}
                      </div>
                    );
                  })()}

                  <form onSubmit={handleSubmit} className="flex flex-col gap-4" noValidate>
                    <div className="flex flex-col gap-1.5">
                      <label className="text-xs font-bold uppercase" style={{ color: 'var(--text-muted)', fontFamily: 'var(--font-display)', letterSpacing: '0.16em' }}>
                        {mfaStrategy === 'backup_code' ? 'Backup code' : 'Verification code'}
                      </label>
                      <input
                        type="text"
                        inputMode={mfaStrategy === 'backup_code' ? 'text' : 'numeric'}
                        autoComplete="one-time-code"
                        maxLength={mfaStrategy === 'backup_code' ? 32 : 6}
                        placeholder={mfaStrategy === 'backup_code' ? 'xxxxxxxx-xxxx' : '000000'}
                        value={mfaCode}
                        disabled={mfaStrategy === 'phone_code' && !phonePrepared}
                        onChange={(e) => { setMfaCode(mfaStrategy === 'backup_code' ? e.target.value : e.target.value.replace(/\D/g, '')); setErrors({}); }}
                        className="w-full rounded px-4 py-3 text-center text-xl font-mono tracking-widest outline-none transition-all disabled:opacity-40"
                        style={{ background: 'var(--bg-deep)', border: '1px solid var(--border-mid)', color: 'var(--text)' }}
                      />
                      {errors['mfaCode'] && <p className="text-xs text-red-400">{errors['mfaCode']}</p>}
                    </div>
                    <button
                      type="submit"
                      disabled={isLoading || (mfaStrategy === 'phone_code' && !phonePrepared) || (mfaStrategy !== 'backup_code' && mfaCode.length < 6) || (mfaStrategy === 'backup_code' && !mfaCode.trim())}
                      className="btn-primary w-full justify-center"
                    >
                      {isLoading ? <><Spinner /> Verifying…</> : 'Verify'}
                    </button>
                  </form>

                  <div className="flex justify-between text-xs" style={{ color: 'var(--text-muted)' }}>
                    {mfaStrategy === 'phone_code' && phonePrepared && (
                      <button type="button" onClick={() => handleSelectMfaStrategy('phone_code')} style={{ color: 'var(--crimson-light)' }} className="hover:underline">Resend</button>
                    )}
                    <span className="flex-1" />
                    <button type="button" onClick={() => { setMode('login'); setMfaCode(''); setErrors({}); setMfaStrategy('totp'); setPhonePrepared(false); }} style={{ color: 'var(--crimson-light)' }} className="hover:underline">
                      Back
                    </button>
                  </div>
                </div>

              ) : (
                /* ── Sign in / Sign up ── */
                <>
                  {/* Tab switcher */}
                  <div className="flex rounded mb-6" style={{ background: 'var(--bg-deep)', padding: '3px' }}>
                    {(['login', 'signup'] as const).map((tab) => (
                      <button
                        key={tab}
                        type="button"
                        onClick={() => switchMode(tab)}
                        className="flex-1 py-2 text-xs font-bold uppercase rounded transition-all"
                        style={{
                          fontFamily: 'var(--font-display)',
                          letterSpacing: '0.14em',
                          background: mode === tab ? 'var(--bg-mid)' : 'transparent',
                          color: mode === tab ? 'var(--text)' : 'var(--text-muted)',
                          border: mode === tab ? '1px solid var(--border-mid)' : '1px solid transparent',
                        }}
                      >
                        {tab === 'login' ? 'Sign in' : 'Create account'}
                      </button>
                    ))}
                  </div>

                  <form onSubmit={handleSubmit} className="flex flex-col gap-4" noValidate>
                    {mode === 'signup' && (
                      <Field label="Full name" name="name" autoComplete="name" placeholder="Alex Morgan"
                        value={formData.name} error={errors['name']} onChange={handleChange} />
                    )}
                    <Field label="Email" name="email" type="email" autoComplete="email" placeholder="you@example.com"
                      value={formData.email} error={errors['email']} onChange={handleChange} />
                    <Field
                      label="Password" name="password" type="password"
                      autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
                      placeholder="••••••••"
                      value={formData.password} error={errors['password']} onChange={handleChange}
                    />
                    {mode === 'signup' && (
                      <Field label="Confirm password" name="confirmPassword" type="password" autoComplete="new-password"
                        placeholder="••••••••" value={formData.confirmPassword} error={errors['confirmPassword']} onChange={handleChange} />
                    )}
                    <button
                      type="submit"
                      disabled={isLoading || (mode === 'login' ? !signInLoaded : !signUpLoaded)}
                      className="btn-primary w-full justify-center mt-1"
                    >
                      {isLoading
                        ? <><Spinner /> {mode === 'login' ? 'Signing in…' : 'Creating account…'}</>
                        : mode === 'login' ? 'Sign in' : 'Create account'
                      }
                    </button>
                  </form>

                  {mode === 'login' && (
                    <p className="mt-4 text-center text-xs" style={{ color: 'var(--text-muted)' }}>
                      <button
                        type="button"
                        style={{ color: 'var(--crimson-light)' }}
                        className="hover:underline"
                        onClick={() => { setMode('forgot'); setErrors({}); }}
                      >
                        Forgot password?
                      </button>
                    </p>
                  )}
                </>
              )}
            </div>

            <p className="text-center mt-6 text-xs" style={{ color: 'var(--text-faint)' }}>
              By signing in you agree to our{' '}
              <span className="hover:underline cursor-pointer" style={{ color: 'var(--text-muted)' }}>Terms</span>{' '}
              and{' '}
              <span className="hover:underline cursor-pointer" style={{ color: 'var(--text-muted)' }}>Privacy Policy</span>.
            </p>
          </div>
    </div>
  );
};

export default LoginPage;
